import { useState, useEffect } from 'react';
import logoSenior from '../../../assets/logo.png';
import { useWizardRequest } from '../../../hooks/services/useWizardRequest';
import IPage from '../../../interfaces/IPage';
import IMedicalCertificate from '../../../interfaces/IMedicalCertificate';
import IEmployee from '../../../interfaces/IEmployee';
import ITypeDocument from '../../../interfaces/ITypeDocument';
import { useNavigate, useLocation } from 'react-router-dom';
import InstallPWA from '../../../components/InstallPWA/InstallPWA';
import { Img, Container, WizardFooter, Link, WizardContent, TimelineContainer, TimelineItem, TimelineDot, JustificationBox, TimelineTitle, TimelineDate, InfoHeader, InfoRow, ContainerInfo } from './DocumentStatus.styled';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';
import { Divider, Typography } from '@mui/material';

interface DocumentStatusProps extends IPage { }

const initialMedicalCertificate: IMedicalCertificate = {
  id: '',
  typeDocumentId: 0,
  employeeId: 0,
  clinicName: '',
  registerNumber: '',
  professionalName: '',
  professionalState: '',
  professionalTypeId: 0,
  diseaseClassifications: [],
  professionalTypeName: '',
  professionalTypeOrganization: '',
  documentDate: '',
  typeTime: '',
  startTime: null,
  endTime: null,
  timeQuantity: 24,
  email: '',
  phoneNumber: '',
  status: 'APPROVED',
  comments: '',
  notes: '',
  protocolNumber: '',
  origin: '',
  statusIntegration: null,
  confirmed: undefined,
  isDraft: undefined,
  confirmedAt: '',
  datetimeIntegraton: null,
  alerts: [],
  createdAt: undefined,
  updatedAt: undefined,
  errorTitle: '',
  errorMessage: '',
  attachments: [
    {
      id: '',
      medicalCertificateId: '',
      attachment: '',
      createdAt: '',
      updatedAt: '',
    },
  ],
};
const initialEmployee: IEmployee = {
  name: '',
  gender: '',
  cpf: '',
  birthday: '',
  admissionDate: '',
  demissionDate: '',
  city: '',
  state: '',
  type: '',
  code: '',
  situation: 0,

  company: {
    name: '',
    code: '',
  },
  subsidiary: {
    name: '',
    code: '',
  },
  local: {
    name: '',
    code: '',
  },
  post: {
    name: '',
    code: '',
  },
  workstation: {
    name: '',
    code: '',
  },
};

const initialTypeDocument: ITypeDocument = {
  id: 0,
  description: '',
  isActive: false,
  order: 0,
  cidRequired: false,
  crmRequired: false,
  hospitalRequired: false,
};
const DocumentStatus = (props: DocumentStatusProps) => {
  window.document.title = props.title;
  const [medicalCertificate, setMedicalCertificate] = useState<IMedicalCertificate>(initialMedicalCertificate);
  const [employee, SetEmployee] = useState<IEmployee>(initialEmployee);
  const [typeDocument, setTypeDocument] = useState<ITypeDocument>(initialTypeDocument);
  const [dateDocument, setDatedocument] = useState<string | null>(null);
  const request = useWizardRequest();
  const toastr = useToastrVanila();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const protocoloUrl = location.pathname.split('/')[2];
    LoadMedicById(protocoloUrl);
  }, []);

  const LoadMedicById = async (Protocolo: string): Promise<void> => {
    request
      .findByProtocolNumberNotAuth(Protocolo)
      .then(result => {
        setMedicalCertificate({ ...result });
        LoadEmployeeById(result.employeeId);
        if (result.typeDocumentId !== null) {
          LoadTypeDocument(result.typeDocumentId);
          ConvertStringDate(result?.documentDate);
        }
      })
      .catch(() => {
        toastr.error('Ocorreu um erro ao Carregar Documento');
        navigate('/');
      });
  };

  const LoadEmployeeById = async (id: number): Promise<void> => {
    request
      .listEmployeeById(id)
      .then(result => {
        SetEmployee({ ...result });
      })
      .catch(error => {
        toastr.error('ocorreu um erro ao carregar colaborador');
      });
  };

  const LoadTypeDocument = async (id: number): Promise<void> => {
    request
      .listTypeDocumentsById(id)
      .then(result => {
        setTypeDocument({ ...result });
      })
      .catch(() => {
        toastr.error('ocorreu um erro ao carregar tipo do documento');
      });
  };
  const ConvertStringDate = async (dateString: string | null): Promise<number> => {
    if (dateString === null) {
      return 0;
    } else {
      const [year, month, day] = dateString.split('-');
      setDatedocument(`${day}/${month}/${year}`);
    }
    return 0;
  };

  return (
    <Container>
      <WizardContent>
        <InstallPWA type="link" />
        <Link onClick={() => navigate('/login')}>Login</Link>
        <ContainerInfo>
          <InfoHeader>
            <Typography variant='h5' >
              Olá, {employee?.name}
            </Typography>
            <Typography variant='overline' gutterBottom>
              Acompanhamento do documento
            </Typography>
          </InfoHeader>

          <InfoRow>
            <Typography sx={{ fontSize: 13 }} color="text.secondary">
              Protocolo
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
              {medicalCertificate?.protocolNumber}
            </Typography>
          </InfoRow>

          <InfoRow>
            <Typography sx={{ fontSize: 13 }} color="text.secondary">
              Tipo
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
              {typeDocument?.description}
            </Typography>
          </InfoRow>

          <Divider sx={{ width: '100%', maxWidth: 320, my: 2 }} />

          <TimelineContainer>
            {/* Step 1 — Documento enviado (sempre concluído) */}
            <TimelineItem>
              <TimelineDot variant="done" />
              <TimelineTitle>Documento enviado</TimelineTitle>
              <TimelineDate>Data do documento:{dateDocument}</TimelineDate>
            </TimelineItem>

            {/* Step 2 — Em análise */}
            <TimelineItem>
              <TimelineDot
                variant={medicalCertificate.status === 'PENDING' ? 'active' : 'done'}
              />
              <TimelineTitle
                color={medicalCertificate.status === 'PENDING' ? '#868686' : undefined}
              >
                {medicalCertificate.status === 'PENDING' ? 'Em análise' : 'Analisado'}
              </TimelineTitle>
            </TimelineItem>

            {/* Step 3 — Status final (só aparece se não for PENDING) */}
            {medicalCertificate.status !== 'PENDING' && (
              <TimelineItem>
                <TimelineDot
                  variant="active"
                  color={
                    medicalCertificate.status === 'APPROVED'
                      ? '#0D860D'
                      : medicalCertificate.status === 'ARCHIVED'
                        ? '#F57C00'
                        : '#FF0D0D'
                  }
                />
                <TimelineTitle
                  color={
                    medicalCertificate.status === 'APPROVED'
                      ? '#0D860D'
                      : medicalCertificate.status === 'ARCHIVED'
                        ? '#F57C00'
                        : '#FF0D0D'
                  }
                >
                  {medicalCertificate.status === 'APPROVED'
                    ? 'APROVADO'
                    : medicalCertificate.status === 'ARCHIVED'
                      ? 'ARQUIVADO'
                      : 'REPROVADO'}
                </TimelineTitle>

                {medicalCertificate.status === 'DISAPPROVED' &&
                  medicalCertificate.events?.length && (
                    <JustificationBox>
                      {medicalCertificate.events[0].description || medicalCertificate.justification}
                    </JustificationBox>
                )}
                
              </TimelineItem>
            )}
          </TimelineContainer>
        </ContainerInfo>
      </WizardContent>
      <div style={{ width: '100%', position: 'absolute', bottom: '0' }}>
        <p style={{ textAlign: 'center' }}>Para mais informações, consulte o RH de sua empresa.</p>
        <WizardFooter>
          <>
            <Img src={logoSenior} alt="Senior" />
            Senior Norte PR
          </>
        </WizardFooter>
      </div>
    </Container>
  );
};

export default DocumentStatus;
